/**
 * Fábrica de texturas placeholder, generadas por código (pixel-art simple,
 * paleta alegre). Objetivo: que ningún agente/fase se bloquee esperando
 * arte real — todo lo visual tiene un placeholder decente desde el día 1.
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en core/": este archivo sí
 * importa Phaser porque su trabajo es, por definición, generar texturas de
 * Phaser (`scene.textures`). No contiene lógica de juego ni se testea con
 * Vitest; el resto de core/ (types, events, rng, state, save) permanece
 * 100% libre de Phaser y testeado.
 */

import Phaser from 'phaser';

/** Claves de todas las texturas generadas, para referenciarlas sin strings sueltos. */
export const TEX = {
  /** Héroe arquero — 2 cuadros de reposo (bob sutil) + 1 de disparo, para animar sin spritesheet. */
  hero: 'tex-hero',
  heroIdle2: 'tex-hero-idle2',
  heroAttack: 'tex-hero-attack',
  enemySlime: 'tex-enemy-slime',
  enemyGoblin: 'tex-enemy-goblin',
  enemyBat: 'tex-enemy-bat',
  enemySpider: 'tex-enemy-spider',
  /** Jefe genérico (fallback para ids "boss-*" sin bioma reconocido). */
  boss: 'tex-boss',
  bossBosque: 'tex-boss-bosque',
  bossOeste: 'tex-boss-oeste',
  bossCripta: 'tex-boss-cripta',
  bossVolcan: 'tex-boss-volcan',
  ball: 'tex-ball',
  barMult: 'tex-bar-mult',
  barMystery: 'tex-bar-mystery',
  barTrampoline: 'tex-bar-trampoline',
  barNegative: 'tex-bar-negative',
  cup: 'tex-cup',
  cupReceiver: 'tex-cup-receiver',
  buttonBase: 'tex-button-base',
  buttonPrimary: 'tex-button-primary',
  iconCoin: 'tex-icon-coin',
  iconGem: 'tex-icon-gem',
  pixel: 'tex-pixel',
} as const;

export type TextureKey = (typeof TEX)[keyof typeof TEX];

/** Paleta base compartida por todos los placeholders. */
const PALETTE = {
  heroSkin: 0xffd9a0,
  heroBody: 0x3f96d6,
  heroBodyDark: 0x2c6ea3,
  slime: 0x6ee87a,
  slimeDark: 0x3fae4c,
  goblin: 0xc9a227,
  goblinDark: 0x8f7314,
  bat: 0x8b5cf6,
  batDark: 0x5b3aa0,
  spider: 0x2e2440,
  spiderLeg: 0x4a3a66,
  spiderEye: 0xff4d4d,
  boss: 0xe0524d,
  bossDark: 0x9e2f2b,
  // jefes por bioma: siluetas y paletas propias para distinguirlos a simple vista.
  bossBosqueBody: 0x6b7a3f,
  bossBosqueDark: 0x3f4a22,
  bossBosqueBark: 0x5c3a22,
  bossOesteBody: 0xc9a05a,
  bossOesteDark: 0x8f6a34,
  bossOesteHat: 0x2a1f18,
  bossCriptaBody: 0xcfd0e0,
  bossCriptaRobe: 0x5b3aa0,
  bossCriptaRobeDark: 0x37215e,
  bossVolcanBody: 0xff5a36,
  bossVolcanDark: 0xb32d16,
  bossVolcanGlow: 0xffd166,
  ball: 0xffffff,
  ballShade: 0xd9dee6,
  multLo: 0x6fcf6f, // verde: multiplicadores bajos
  multHi: 0x9aa832, // oliva: multiplicadores altos
  mystery: 0x8b5cf6,
  mysteryDark: 0x5b3aa0,
  trampoline: 0x3fb2ff,
  trampolineDark: 0x1f7fbf,
  negative: 0xe0524d,
  negativeDark: 0x9e2f2b,
  cup: 0x8ecbff,
  cupOutline: 0x2c6ea3,
  buttonBase: 0x3a2f5c,
  buttonBaseDark: 0x241c3d,
  buttonPrimary: 0xffb703,
  buttonPrimaryDark: 0xc98600,
  coin: 0xffd166,
  coinDark: 0xc98600,
  gem: 0x6fcfff,
  gemDark: 0x2c6ea3,
  outline: 0x1b1030,
} as const;

/** Interpola el color de una barra multiplicadora según su valor (×2..×7). */
export function multBarColor(value: number): number {
  const t = Phaser.Math.Clamp((value - 2) / (7 - 2), 0, 1);
  const color = Phaser.Display.Color.Interpolate.ColorWithColor(
    Phaser.Display.Color.ValueToColor(PALETTE.multLo),
    Phaser.Display.Color.ValueToColor(PALETTE.multHi),
    100,
    Math.round(t * 100)
  );
  return Phaser.Display.Color.GetColor(color.r, color.g, color.b);
}

/** Dibuja y hornea una textura, limpiando el objeto Graphics temporal. */
function bake(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
  draw: (g: Phaser.GameObjects.Graphics) => void
): void {
  if (scene.textures.exists(key)) return;
  const g = scene.add.graphics();
  draw(g);
  g.generateTexture(key, width, height);
  g.destroy();
}

/**
 * Héroe arquero, vista lateral, 16x16. Cuerpo y cabeza fijos; solo cambian
 * la altura del arco y la cuerda/flecha entre cuadros, para poder animar
 * (reposo con bob sutil + disparo) sin depender de un spritesheet.
 *
 * Todo se dibuja con rellenos (`fillRect`/`fillTriangle`): nada de
 * `lineStyle`/`arc` — a 16px esas líneas finas salen antialiased y se ven
 * como una mancha borrosa en vez de un trazo nítido de pixel-art.
 *
 * `armLift`: 0 = arco en reposo (cuadro A), 1 = arco un pixel más arriba
 * (cuadro B, bob sutil de reposo).
 * `drawn`: true = cuerda tensada hacia el cuerpo y flecha larga (disparo).
 */
function drawHeroArcher(g: Phaser.GameObjects.Graphics, armLift: 0 | 1, drawn: boolean): void {
  const top = armLift === 0 ? 3 : 2; // techo del arco: sube 1px en el cuadro B
  // cuerpo: cabeza, torso, piernas.
  g.fillStyle(PALETTE.outline, 1);
  g.fillRect(3, 1, 8, 14);
  g.fillStyle(PALETTE.heroSkin, 1);
  g.fillRect(4, 2, 6, 4); // cabeza
  g.fillStyle(PALETTE.heroBody, 1);
  g.fillRect(4, 6, 6, 6); // torso
  g.fillStyle(PALETTE.heroBodyDark, 1);
  g.fillRect(4, 11, 6, 3); // piernas
  // carcaj de flechas a la espalda
  g.fillStyle(PALETTE.heroBodyDark, 1);
  g.fillRect(2, 3, 2, 6);
  g.fillStyle(0xdddddd, 1);
  g.fillRect(2, 2, 2, 2);

  // arco: 3 bloques apilados que forman una "C" mirando a la derecha (curva
  // sólida, sin líneas finas).
  g.fillStyle(0xe4e0f2, 1);
  g.fillRect(12, top, 2, 3);
  g.fillRect(13, top + 3, 1, 4);
  g.fillRect(12, top + 7, 2, 3);

  // cuerda: relleno sólido de 1px (nunca `lineBetween`, para que no se
  // difumine). En reposo toca las puntas del arco; al disparar se tensa
  // hacia el cuerpo.
  const stringX = drawn ? 10 : 12;
  g.fillStyle(0xf4f0ff, 1);
  g.fillRect(stringX, top, 1, 10);

  // flecha: nockeada en la cuerda, con la punta siempre anclada cerca del
  // borde del arco — al tensar la cuerda hacia atrás, el asta se alarga.
  const headX = 15;
  const arrowY = top + 4;
  g.fillStyle(PALETTE.buttonPrimary, 1);
  g.fillRect(stringX + 1, arrowY, Math.max(1, headX - stringX - 1), 1.4);
  g.fillTriangle(headX, arrowY - 1, headX, arrowY + 2.4, headX + 1.6, arrowY + 0.7);
}

function drawHeroIdleA(g: Phaser.GameObjects.Graphics): void {
  drawHeroArcher(g, 0, false);
}

function drawHeroIdleB(g: Phaser.GameObjects.Graphics): void {
  drawHeroArcher(g, 1, false);
}

function drawHeroAttack(g: Phaser.GameObjects.Graphics): void {
  drawHeroArcher(g, 1, true);
}

function drawBlobEnemy(
  g: Phaser.GameObjects.Graphics,
  main: number,
  dark: number,
  size: number
): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(0, size * 0.25, size, size * 0.75, size * 0.2);
  g.fillStyle(main, 1);
  g.fillRoundedRect(1, size * 0.3, size - 2, size * 0.7 - 2, size * 0.18);
  g.fillStyle(dark, 1);
  g.fillRect(1, size - 4, size - 2, 3);
  g.fillStyle(PALETTE.outline, 1);
  g.fillCircle(size * 0.35, size * 0.55, 1.4);
  g.fillCircle(size * 0.65, size * 0.55, 1.4);
}

function drawGoblinEnemy(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRect(size * 0.2, size * 0.1, size * 0.6, size * 0.85);
  g.fillStyle(PALETTE.goblin, 1);
  g.fillRect(size * 0.25, size * 0.15, size * 0.5, size * 0.35); // cabeza
  g.fillStyle(PALETTE.goblinDark, 1);
  g.fillRect(size * 0.22, size * 0.5, size * 0.56, size * 0.4); // cuerpo
  g.fillStyle(0x7a5a12, 1);
  g.fillRect(size * 0.6, size * 0.35, size * 0.25, size * 0.08); // lanza
}

function drawBatEnemy(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillTriangle(0, size * 0.4, size * 0.35, size * 0.1, size * 0.35, size * 0.65);
  g.fillTriangle(size, size * 0.4, size * 0.65, size * 0.1, size * 0.65, size * 0.65);
  g.fillStyle(PALETTE.bat, 1);
  g.fillCircle(size * 0.5, size * 0.45, size * 0.28);
  g.fillStyle(PALETTE.batDark, 1);
  g.fillCircle(size * 0.5, size * 0.6, size * 0.16);
}

/** Araña 4º tipo de enemigo común: silueta muy distinta (patas finas, cuerpo bajo y ancho). */
function drawSpiderEnemy(g: Phaser.GameObjects.Graphics, size: number): void {
  const cx = size * 0.5;
  const cy = size * 0.6;
  // patas: 3 por lado, en ángulo, finas.
  g.lineStyle(Math.max(1, size * 0.07), PALETTE.spiderLeg, 1);
  const legAnglesDeg = [-55, -15, 25];
  for (const deg of legAnglesDeg) {
    const rad = Phaser.Math.DegToRad(deg);
    const legLen = size * 0.42;
    g.lineBetween(cx, cy, cx - Math.cos(rad) * legLen, cy + Math.sin(rad) * legLen);
    g.lineBetween(cx, cy, cx + Math.cos(rad) * legLen, cy + Math.sin(rad) * legLen);
  }
  // abdomen (grande, trasero) + cefalotórax (pequeño, delantero).
  g.fillStyle(PALETTE.outline, 1);
  g.fillEllipse(cx, cy, size * 0.5, size * 0.36);
  g.fillStyle(PALETTE.spider, 1);
  g.fillEllipse(cx, cy, size * 0.44, size * 0.3);
  g.fillStyle(PALETTE.outline, 1);
  g.fillCircle(cx - size * 0.28, cy - size * 0.06, size * 0.14);
  g.fillStyle(PALETTE.spider, 1);
  g.fillCircle(cx - size * 0.28, cy - size * 0.06, size * 0.11);
  // ojos rojos
  g.fillStyle(PALETTE.spiderEye, 1);
  g.fillCircle(cx - size * 0.33, cy - size * 0.08, size * 0.035);
  g.fillCircle(cx - size * 0.23, cy - size * 0.08, size * 0.035);
}

function drawBoss(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(0, size * 0.12, size, size * 0.88, size * 0.12);
  g.fillStyle(PALETTE.boss, 1);
  g.fillRoundedRect(size * 0.06, size * 0.16, size * 0.88, size * 0.5, size * 0.1);
  g.fillStyle(PALETTE.bossDark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.62, size * 0.8, size * 0.3, size * 0.08);
  // cuernos
  g.fillStyle(0xf2f2f2, 1);
  g.fillTriangle(
    size * 0.18,
    size * 0.18,
    size * 0.28,
    size * 0.18,
    size * 0.14,
    0
  );
  g.fillTriangle(
    size * 0.72,
    size * 0.18,
    size * 0.82,
    size * 0.18,
    size * 0.86,
    0
  );
  // ojos
  g.fillStyle(0xfff176, 1);
  g.fillCircle(size * 0.36, size * 0.4, size * 0.05);
  g.fillCircle(size * 0.64, size * 0.4, size * 0.05);
}

/** Jefe del Bosque Otoñal: treante robusto, corteza + ramas a modo de cuernos. */
function drawBossBosque(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(size * 0.04, size * 0.14, size * 0.92, size * 0.86, size * 0.08);
  g.fillStyle(PALETTE.bossBosqueBark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.18, size * 0.8, size * 0.56, size * 0.06);
  g.fillStyle(PALETTE.bossBosqueBody, 1);
  g.fillRoundedRect(size * 0.14, size * 0.22, size * 0.72, size * 0.4, size * 0.05);
  g.fillStyle(PALETTE.bossBosqueDark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.66, size * 0.8, size * 0.3, size * 0.06);
  // ramas (cuernos irregulares, no simétricas como los cuernos del genérico)
  g.fillStyle(PALETTE.bossBosqueBark, 1);
  g.fillTriangle(size * 0.16, size * 0.2, size * 0.24, size * 0.2, size * 0.1, 0);
  g.fillTriangle(size * 0.2, size * 0.16, size * 0.26, size * 0.16, size * 0.3, 0);
  g.fillTriangle(size * 0.78, size * 0.2, size * 0.86, size * 0.2, size * 0.92, 0);
  // ojos musgo
  g.fillStyle(0xd9f27a, 1);
  g.fillCircle(size * 0.36, size * 0.42, size * 0.045);
  g.fillCircle(size * 0.64, size * 0.42, size * 0.045);
}

/** Jefe del Lejano Oeste: forajido/gólem con sombrero de ala ancha y poncho. */
function drawBossOeste(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(size * 0.06, size * 0.28, size * 0.88, size * 0.72, size * 0.08);
  // sombrero de ala ancha: silueta muy reconocible, distinta a cuernos.
  g.fillStyle(PALETTE.bossOesteHat, 1);
  g.fillEllipse(size * 0.5, size * 0.22, size * 0.86, size * 0.14);
  g.fillRoundedRect(size * 0.32, size * 0.04, size * 0.36, size * 0.2, size * 0.05);
  // poncho + cuerpo
  g.fillStyle(PALETTE.bossOesteBody, 1);
  g.fillRoundedRect(size * 0.12, size * 0.32, size * 0.76, size * 0.44, size * 0.06);
  g.fillStyle(PALETTE.bossOesteDark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.72, size * 0.8, size * 0.26, size * 0.06);
  // franjas del poncho
  g.fillStyle(PALETTE.bossOesteHat, 1);
  g.fillRect(size * 0.16, size * 0.5, size * 0.68, size * 0.06);
  // ojos bajo el ala del sombrero (en sombra)
  g.fillStyle(0xffd166, 1);
  g.fillCircle(size * 0.4, size * 0.36, size * 0.04);
  g.fillCircle(size * 0.6, size * 0.36, size * 0.04);
}

/** Jefe de la Cripta: aparición encapuchada, alta y delgada, túnica violeta. */
function drawBossCripta(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillTriangle(size * 0.5, 0, size * 0.94, size, size * 0.06, size);
  // túnica (silueta triangular, muy distinta a los bloques de los otros jefes)
  g.fillStyle(PALETTE.bossCriptaRobeDark, 1);
  g.fillTriangle(size * 0.5, size * 0.06, size * 0.88, size * 0.96, size * 0.12, size * 0.96);
  g.fillStyle(PALETTE.bossCriptaRobe, 1);
  g.fillTriangle(size * 0.5, size * 0.1, size * 0.74, size * 0.9, size * 0.26, size * 0.9);
  // capucha + rostro óseo
  g.fillStyle(PALETTE.outline, 1);
  g.fillCircle(size * 0.5, size * 0.22, size * 0.16);
  g.fillStyle(PALETTE.bossCriptaBody, 1);
  g.fillCircle(size * 0.5, size * 0.24, size * 0.12);
  // ojos vacíos brillantes
  g.fillStyle(0x9be8ff, 1);
  g.fillCircle(size * 0.44, size * 0.24, size * 0.035);
  g.fillCircle(size * 0.56, size * 0.24, size * 0.035);
  // manos óseas asomando de las mangas
  g.fillStyle(PALETTE.bossCriptaBody, 1);
  g.fillCircle(size * 0.22, size * 0.72, size * 0.06);
  g.fillCircle(size * 0.78, size * 0.72, size * 0.06);
}

/** Jefe del Volcán: demonio de magma, cuernos grandes y grietas incandescentes. */
function drawBossVolcan(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(size * 0.06, size * 0.16, size * 0.88, size * 0.84, size * 0.14);
  g.fillStyle(PALETTE.bossVolcanDark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.2, size * 0.8, size * 0.56, size * 0.12);
  g.fillStyle(PALETTE.bossVolcanBody, 1);
  g.fillRoundedRect(size * 0.14, size * 0.24, size * 0.72, size * 0.4, size * 0.1);
  g.fillStyle(PALETTE.bossVolcanDark, 1);
  g.fillRoundedRect(size * 0.1, size * 0.7, size * 0.8, size * 0.3, size * 0.1);
  // cuernos grandes y curvos (más largos que el genérico, apuntando hacia afuera)
  g.fillStyle(0x2a1414, 1);
  g.fillTriangle(size * 0.14, size * 0.22, size * 0.3, size * 0.28, size * 0.01, size * 0.02);
  g.fillTriangle(size * 0.86, size * 0.22, size * 0.7, size * 0.28, size * 0.99, size * 0.02);
  // grietas incandescentes
  g.fillStyle(PALETTE.bossVolcanGlow, 1);
  g.fillRect(size * 0.3, size * 0.3, size * 0.05, size * 0.3);
  g.fillRect(size * 0.65, size * 0.35, size * 0.05, size * 0.22);
  // ojos
  g.fillStyle(PALETTE.bossVolcanGlow, 1);
  g.fillCircle(size * 0.38, size * 0.42, size * 0.05);
  g.fillCircle(size * 0.62, size * 0.42, size * 0.05);
}

function drawBall(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillCircle(size / 2, size / 2, size / 2);
  g.fillStyle(PALETTE.ball, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 1);
  g.fillStyle(PALETTE.ballShade, 1);
  g.fillCircle(size * 0.62, size * 0.62, size * 0.18);
}

function drawPixelBar(
  g: Phaser.GameObjects.Graphics,
  width: number,
  height: number,
  fill: number,
  dark: number,
  glyph?: (g: Phaser.GameObjects.Graphics) => void
): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(0, 0, width, height, 4);
  g.fillStyle(fill, 1);
  g.fillRoundedRect(1, 1, width - 2, height - 2, 3);
  g.fillStyle(dark, 1);
  g.fillRoundedRect(1, height - 5, width - 2, 4, 3);
  glyph?.(g);
}

function drawCup(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
  const topW = width;
  const bottomW = width * 0.62;
  g.fillStyle(PALETTE.cupOutline, 1);
  g.fillTriangle(0, 0, topW, 0, (topW - bottomW) / 2, height);
  g.fillTriangle(topW, 0, (topW - bottomW) / 2, height, (topW + bottomW) / 2, height);
  g.fillStyle(PALETTE.cup, 0.85);
  g.fillTriangle(2, 2, topW - 2, 2, (topW - bottomW) / 2 + 1, height - 2);
  g.fillTriangle(
    topW - 2,
    2,
    (topW - bottomW) / 2 + 1,
    height - 2,
    (topW + bottomW) / 2 - 1,
    height - 2
  );
}

function drawButton(g: Phaser.GameObjects.Graphics, width: number, height: number, fill: number, dark: number): void {
  g.fillStyle(PALETTE.outline, 1);
  g.fillRoundedRect(0, 0, width, height, 10);
  g.fillStyle(fill, 1);
  g.fillRoundedRect(2, 2, width - 4, height - 4, 8);
  g.fillStyle(dark, 1);
  g.fillRoundedRect(2, height - 10, width - 4, 8, 8);
  g.fillStyle(0xffffff, 0.18);
  g.fillRoundedRect(4, 4, width - 8, (height - 8) * 0.4, 6);
}

function drawCoinIcon(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.coinDark, 1);
  g.fillCircle(size / 2, size / 2, size / 2);
  g.fillStyle(PALETTE.coin, 1);
  g.fillCircle(size / 2, size / 2 - 1, size / 2 - 1.5);
  g.fillStyle(PALETTE.coinDark, 1);
  g.fillRect(size * 0.42, size * 0.3, size * 0.16, size * 0.4);
}

function drawGemIcon(g: Phaser.GameObjects.Graphics, size: number): void {
  g.fillStyle(PALETTE.gemDark, 1);
  g.fillTriangle(size / 2, 0, size, size * 0.4, 0, size * 0.4);
  g.fillTriangle(0, size * 0.4, size, size * 0.4, size / 2, size);
  g.fillStyle(PALETTE.gem, 1);
  g.fillTriangle(size / 2, size * 0.12, size * 0.85, size * 0.42, size * 0.15, size * 0.42);
}

/**
 * Genera (una sola vez) todas las texturas placeholder en el `TextureManager`
 * de la escena/juego dado. Idempotente: si una clave ya existe, no la
 * regenera. Llamar típicamente desde la escena Preload.
 */
export class TextureFactory {
  static generateAll(scene: Phaser.Scene): void {
    bake(scene, TEX.hero, 16, 16, drawHeroIdleA);
    bake(scene, TEX.heroIdle2, 16, 16, drawHeroIdleB);
    bake(scene, TEX.heroAttack, 16, 16, drawHeroAttack);
    bake(scene, TEX.enemySlime, 16, 16, (g) => drawBlobEnemy(g, PALETTE.slime, PALETTE.slimeDark, 16));
    bake(scene, TEX.enemyGoblin, 16, 16, (g) => drawGoblinEnemy(g, 16));
    bake(scene, TEX.enemyBat, 16, 16, (g) => drawBatEnemy(g, 16));
    bake(scene, TEX.enemySpider, 16, 16, (g) => drawSpiderEnemy(g, 16));
    bake(scene, TEX.boss, 48, 48, (g) => drawBoss(g, 48));
    bake(scene, TEX.bossBosque, 48, 48, (g) => drawBossBosque(g, 48));
    bake(scene, TEX.bossOeste, 48, 48, (g) => drawBossOeste(g, 48));
    bake(scene, TEX.bossCripta, 48, 48, (g) => drawBossCripta(g, 48));
    bake(scene, TEX.bossVolcan, 48, 48, (g) => drawBossVolcan(g, 48));
    bake(scene, TEX.ball, 8, 8, (g) => drawBall(g, 8));

    bake(scene, TEX.barMult, 64, 20, (g) => drawPixelBar(g, 64, 20, PALETTE.multLo, PALETTE.multLo));
    bake(scene, TEX.barMystery, 64, 20, (g) =>
      drawPixelBar(g, 64, 20, PALETTE.mystery, PALETTE.mysteryDark, (gg) => {
        gg.fillStyle(0xffffff, 1);
        gg.fillRect(28, 5, 3, 6);
        gg.fillRect(28, 13, 3, 3);
      })
    );
    bake(scene, TEX.barTrampoline, 64, 20, (g) =>
      drawPixelBar(g, 64, 20, PALETTE.trampoline, PALETTE.trampolineDark, (gg) => {
        gg.fillStyle(0xffffff, 1);
        gg.fillTriangle(32, 4, 26, 12, 38, 12);
        gg.fillRect(30, 12, 4, 4);
      })
    );
    bake(scene, TEX.barNegative, 64, 20, (g) =>
      drawPixelBar(g, 64, 20, PALETTE.negative, PALETTE.negativeDark, (gg) => {
        gg.lineStyle(3, 0xffffff, 1);
        gg.lineBetween(26, 5, 38, 15);
        gg.lineBetween(38, 5, 26, 15);
      })
    );

    bake(scene, TEX.cup, 32, 36, (g) => drawCup(g, 32, 36));
    bake(scene, TEX.cupReceiver, 64, 40, (g) => drawCup(g, 64, 40));

    bake(scene, TEX.buttonBase, 160, 48, (g) =>
      drawButton(g, 160, 48, PALETTE.buttonBase, PALETTE.buttonBaseDark)
    );
    bake(scene, TEX.buttonPrimary, 200, 56, (g) =>
      drawButton(g, 200, 56, PALETTE.buttonPrimary, PALETTE.buttonPrimaryDark)
    );

    bake(scene, TEX.iconCoin, 20, 20, (g) => drawCoinIcon(g, 20));
    bake(scene, TEX.iconGem, 20, 20, (g) => drawGemIcon(g, 20));

    bake(scene, TEX.pixel, 1, 1, (g) => {
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 1, 1);
    });
  }
}
